import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { X, Image, Video, Upload, Loader, Trash2, Film } from 'lucide-react';
import { baseUrl } from '../config';

/**
 * PostCreator - Component for creating posts with SINGLE media upload
 */
const PostCreator = ({ communityId, communityName, onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({
        title: '',
        content: ''
    });

    // ✅ SIMPLIFIED: Single media file state
    const [mediaFile, setMediaFile] = useState(null);
    const [mediaPreview, setMediaPreview] = useState(null);
    const [mediaType, setMediaType] = useState(null); // 'image' or 'video'

    const [loading, setLoading] = useState(false);
    const [uploadingMedia, setUploadingMedia] = useState(false);
    const [errors, setErrors] = useState({});

    const validateForm = () => {
        const newErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }

        if (!formData.content.trim()) {
            newErrors.content = 'Content is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // ✅ SIMPLIFIED: Single media handler
    const handleMediaSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Determine media type
        const isImage = file.type.startsWith('image/');
        const isVideo = file.type.startsWith('video/');

        if (!isImage && !isVideo) {
            setErrors({ ...errors, media: 'Please select an image or video file' });
            return;
        }

        // Validate file size
        const maxSize = isImage ? 10 * 1024 * 1024 : 100 * 1024 * 1024; // 10MB for images, 100MB for videos
        if (file.size > maxSize) {
            const maxSizeLabel = isImage ? '10MB' : '100MB';
            setErrors({ ...errors, media: `File must be less than ${maxSizeLabel}` });
            return;
        }

        // Set media
        setMediaFile(file);
        setMediaType(isImage ? 'image' : 'video');
        setMediaPreview(URL.createObjectURL(file));
        setErrors({ ...errors, media: undefined });
    };

    // ✅ Remove media
    const removeMedia = () => {
        if (mediaPreview) URL.revokeObjectURL(mediaPreview);
        setMediaFile(null);
        setMediaPreview(null);
        setMediaType(null);
    };

    // ✅ Upload file to backend
    const uploadFile = async (file, fileType) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('file_type', fileType);
        formData.append('user_id', 0);

        try {
            const response = await fetch(`${baseUrl}/upload/direct`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                },
                body: formData
            });

            if (response.ok) {
                const data = await response.json();
                return data.public_url;
            } else {
                const error = await response.json();
                throw new Error(error.detail || 'Upload failed');
            }
        } catch (error) {
            throw error;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setLoading(true);

        try {
            const token = localStorage.getItem('access_token');

            // ✅ Upload media if present
            let mediaUrl = null;
            let uploadMediaType = null;

            if (mediaFile) {
                setUploadingMedia(true);
                try {
                    mediaUrl = await uploadFile(mediaFile, mediaType);
                    uploadMediaType = mediaType;
                } catch (uploadError) {
                    setErrors({ submit: 'Failed to upload media: ' + uploadError.message });
                    return;
                } finally {
                    setUploadingMedia(false);
                }
            }

            // ✅ Create post with FormData
            const formDataToSend = new FormData();
            formDataToSend.append('title', formData.title.trim());
            formDataToSend.append('content', formData.content.trim());

            // Set appropriate URL based on media type
            if (uploadMediaType === 'image') {
                formDataToSend.append('thumbnail_url', mediaUrl);
                formDataToSend.append('media_type', 'image');
            } else if (uploadMediaType === 'video') {
                formDataToSend.append('video_url', mediaUrl);
                formDataToSend.append('media_type', 'video');
            }

            const response = await fetch(
                `${baseUrl}/community-post/${communityId}/create`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    body: formDataToSend
                }
            );

            if (response.ok) {
                const newPost = await response.json();
                onSuccess(newPost);
            } else {
                const errorData = await response.json();

                let errorMessage = 'Failed to create post';
                if (errorData.detail) {
                    if (Array.isArray(errorData.detail)) {
                        errorMessage = errorData.detail
                            .map(err => `${err.loc.join('.')}: ${err.msg}`)
                            .join(', ');
                    } else {
                        errorMessage = errorData.detail;
                    }
                }
                setErrors({ submit: errorMessage });
            }
        } catch (error) {
            setErrors({
                submit: error.message || 'An error occurred while creating the post'
            });
        } finally {
            setLoading(false);
            setUploadingMedia(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-xl p-8 mb-8 border-2 border-teal-200">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <Film className="h-6 w-6 text-teal-600" />
                        Create Post in {communityName}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                        Share your work with the community
                    </p>
                </div>
                <button
                    onClick={onCancel}
                    className="text-gray-400 hover:text-gray-600 transition"
                >
                    <X className="h-6 w-6" />
                </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Title */}
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Post Title *
                    </label>
                    <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => handleChange('title', e.target.value)}
                        placeholder="Enter a catchy title for your post"
                        className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 transition ${
                            errors.title ? 'border-red-500' : 'border-gray-300'
                        }`}
                        disabled={loading}
                    />
                    {errors.title && (
                        <p className="text-red-500 text-sm mt-1">{errors.title}</p>
                    )}
                </div>

                {/* Content */}
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Content *
                    </label>
                    <textarea
                        value={formData.content}
                        onChange={(e) => handleChange('content', e.target.value)}
                        placeholder="Describe your project, share behind-the-scenes, or tell your story..."
                        rows={6}
                        className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 transition resize-none ${
                            errors.content ? 'border-red-500' : 'border-gray-300'
                        }`}
                        disabled={loading}
                    />
                    {errors.content && (
                        <p className="text-red-500 text-sm mt-1">{errors.content}</p>
                    )}
                </div>

                {/* ✅ SINGLE MEDIA UPLOAD */}
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <Film className="h-4 w-4 text-teal-600" />
                        Media (Optional) - Image or Video
                    </label>

                    {!mediaPreview ? (
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-teal-500 transition">
                            <input
                                type="file"
                                id="media-upload"
                                accept="image/*,video/*"
                                onChange={handleMediaSelect}
                                className="hidden"
                                disabled={loading}
                            />
                            <label
                                htmlFor="media-upload"
                                className="cursor-pointer flex flex-col items-center"
                            >
                                <div className="flex gap-4 mb-3">
                                    <Image className="h-12 w-12 text-gray-400" />
                                    <Video className="h-12 w-12 text-gray-400" />
                                </div>
                                <span className="text-base font-medium text-gray-700 mb-2">
                                    Click to upload Image or Video
                                </span>
                                <span className="text-xs text-gray-500">
                                    Images: PNG, JPG, GIF up to 10MB
                                </span>
                                <span className="text-xs text-gray-500">
                                    Videos: MP4, MOV, AVI up to 100MB
                                </span>
                            </label>
                        </div>
                    ) : (
                        <div className="relative border-2 border-teal-200 rounded-lg p-4">
                            {/* Display preview based on media type */}
                            {mediaType === 'image' ? (
                                <img
                                    src={mediaPreview}
                                    alt="Media preview"
                                    className="w-full h-64 object-cover rounded-lg"
                                />
                            ) : (
                                <video
                                    src={mediaPreview}
                                    controls
                                    className="w-full h-64 object-cover rounded-lg"
                                />
                            )}

                            {/* Media Type Badge */}
                            <div className="absolute top-6 left-6 bg-teal-600 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                                {mediaType === 'image' ? (
                                    <>
                                        <Image className="h-3 w-3" />
                                        Image
                                    </>
                                ) : (
                                    <>
                                        <Video className="h-3 w-3" />
                                        Video
                                    </>
                                )}
                            </div>

                            {/* Remove button */}
                            <button
                                type="button"
                                onClick={removeMedia}
                                className="absolute top-6 right-6 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition shadow-lg"
                                disabled={loading}
                            >
                                <Trash2 className="h-4 w-4" />
                            </button>

                            {/* Upload progress */}
                            {uploadingMedia && (
                                <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex flex-col items-center justify-center">
                                    <Loader className="h-10 w-10 text-white animate-spin mb-2" />
                                    <span className="text-white text-sm font-medium">
                                        Uploading {mediaType}...
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {errors.media && (
                        <p className="text-red-500 text-sm mt-1">{errors.media}</p>
                    )}
                </div>

                {/* Submit Error */}
                {errors.submit && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-red-700 text-sm">{errors.submit}</p>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-4 pt-4">
                    <button
                        type="submit"
                        disabled={loading || uploadingMedia}
                        className="flex-1 bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading || uploadingMedia ? (
                            <>
                                <Loader className="h-5 w-5 animate-spin" />
                                {uploadingMedia ? `Uploading ${mediaType}...` : 'Creating Post...'}
                            </>
                        ) : (
                            <>
                                <Upload className="h-5 w-5" />
                                Publish Post
                            </>
                        )}
                    </button>

                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={loading || uploadingMedia}
                        className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Cancel
                    </button>
                </div>
            </form>

            {/* Tips */}
            <div className="mt-6 bg-teal-50 border border-teal-200 rounded-lg p-4">
                <h4 className="font-semibold text-teal-800 mb-2">💡 Tips for Great Posts</h4>
                <ul className="text-sm text-teal-700 space-y-1">
                    <li>• Upload either an image OR a video (not both)</li>
                    <li>• Use high-quality images (1920x1080 recommended)</li>
                    <li>• Keep videos under 100MB for best performance</li>
                    <li>• Write descriptive titles that capture attention</li>
                    <li>• Share your creative process and insights</li>
                </ul>
            </div>
        </div>
    );
};

PostCreator.propTypes = {
    communityId: PropTypes.number.isRequired,
    communityName: PropTypes.string.isRequired,
    onSuccess: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired
};

export default PostCreator;
